export default function Home() {
  return (
    <main className="min-h-screen">
      <SectionIntro />
      <SectionEngineeringFocus />
      <SectionContact />
    </main>
  );
}

function SectionIntro() {
  return (
    <section className="relative bg-background-50 py-28 md:py-36 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <span className="inline-block bg-white text-foreground-800 text-sm font-medium px-6 py-2.5 rounded-full whitespace-nowrap">
          Engineering Excellence
        </span>

        <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl text-foreground-950 leading-none tracking-tight mt-12 md:mt-14 max-w-4xl">
          Zotterman
          <br />
          Consulting AB
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 mt-24 md:mt-28">
          <div>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed">
              Designing and developing modern digital software solutions for the iOS platform and related technologies with a focus on long-term technical quality.
            </p>
          </div>
          <div>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed">
              Building scalable, maintainable, and high-performance systems using clean architecture principles and user-centered design methodology.
            </p>
          </div>
          <div>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed">
              Following modern software engineering practices including performance optimization, stability assurance, and continuous technical improvement.
            </p>
          </div>
          <div>
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed">
              Emphasizing continuous innovation and adoption of up-to-date engineering standards to deliver reliable and future-ready IT solutions.
            </p>
          </div>
        </div>


      </div>
    </section>
  );
}

function SectionEngineeringFocus() {
  const cards = [
    {
      title: 'Scalable Architecture',
      subtitle: 'Clean, maintainable system design built to grow with evolving business needs and technical demands over time.',
      image: 'https://readdy.ai/api/search-image?query=Abstract%20geometric%20composition%20of%20interconnected%20nodes%20and%20clean%20architectural%20lines%20suggesting%20system%20design%20and%20scalable%20engineering%2C%20warm%20olive%20and%20cream%20tones%20with%20subtle%20amber%20highlights%2C%20minimalist%20professional%20aesthetic%2C%20soft%20ambient%20lighting%2C%20no%20text%20no%20people%20no%20screens%20no%20logos%2C%20high%20detail%20editorial%20quality%2C%20depth%20and%20dimension%20through%20layered%20translucent%20geometric%20forms&width=800&height=800&seq=zotterman-arch-01&orientation=squarish',
    },
    {
      title: 'Performance Optimization',
      subtitle: 'High-efficiency solutions engineered for speed, stability, and seamless user experiences across every interaction.',
      image: 'https://readdy.ai/api/search-image?query=Abstract%20flowing%20smooth%20curves%20and%20elegant%20waveform%20patterns%20evoking%20performance%20optimization%20and%20data%20efficiency%2C%20warm%20earth%20tones%20with%20gentle%20golden%20amber%20gradients%2C%20clean%20minimalist%20composition%20with%20depth%2C%20professional%20technical%20aesthetic%2C%20soft%20atmospheric%20lighting%20with%20subtle%20glow%20effects%2C%20no%20text%20no%20people%20no%20screens%20no%20logos%2C%20high%20detail%20editorial%20quality%2C%20harmonious%20flowing%20movement%20suggesting%20speed%20and%20precision&width=800&height=800&seq=zotterman-perf-02&orientation=squarish',
    },
    {
      title: 'Continuous Innovation',
      subtitle: 'Evolving engineering processes and adopting emerging standards to stay ahead in a rapidly changing technology landscape.',
      image: 'https://readdy.ai/api/search-image?query=Abstract%20layered%20translucent%20organic%20geometric%20shapes%20evolving%20and%20transforming%20suggesting%20continuous%20innovation%20and%20technological%20progress%2C%20warm%20olive%20and%20sage%20tones%20with%20soft%20cream%20highlights%2C%20minimalist%20modern%20composition%2C%20professional%20engineering%20aesthetic%2C%20soft%20diffused%20lighting%20with%20gentle%20depth%20of%20field%2C%20no%20text%20no%20people%20no%20screens%20no%20logos%2C%20high%20detail%20editorial%20quality%2C%20serene%20and%20forward%20looking%20atmosphere&width=800&height=800&seq=zotterman-innov-03&orientation=squarish',
    },
  ];

  return (
    <section className="relative bg-white py-24 md:py-32 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-heading text-4xl md:text-5xl text-foreground-950 text-center leading-tight">
          Engineering Focus
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 mt-16 md:mt-20">
          {cards.map((card) => (
            <div
              key={card.title}
              className="relative rounded-2xl overflow-hidden aspect-square group cursor-default"
            >
              <img
                src={card.image}
                alt={card.title}
                title={card.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/5" />
              <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                <h3 className="font-heading text-2xl md:text-3xl text-white leading-tight">
                  {card.title}
                </h3>
                <p className="text-white/80 text-sm md:text-base mt-4 leading-relaxed max-w-xs">
                  {card.subtitle}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionContact() {
  return (
    <section className="relative bg-background-50 py-28 md:py-36 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <span className="inline-block bg-white text-foreground-800 text-sm font-medium px-6 py-2.5 rounded-full whitespace-nowrap">
          Contact
        </span>

        <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl text-foreground-950 leading-none tracking-tight mt-12 md:mt-14 max-w-3xl">
          Get in
          <br />
          Touch
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 mt-20 md:mt-24">
          <div>
            <p className="text-foreground-500 text-xs md:text-sm uppercase tracking-widest font-medium mb-3">Address</p>
            <p className="text-foreground-800 text-base md:text-lg leading-relaxed">
              Kruthornsvagen 28
            </p>
          </div>
          <div>
            <p className="text-foreground-500 text-xs md:text-sm uppercase tracking-widest font-medium mb-3">Location</p>
            <p className="text-foreground-800 text-base md:text-lg leading-relaxed">
              Sollentuna
              <br />
              192 32
              <br />
              Sweden
            </p>
          </div>
          <div>
            <p className="text-foreground-500 text-xs md:text-sm uppercase tracking-widest font-medium mb-3">Phone</p>
            <p className="text-foreground-800 text-base md:text-lg leading-relaxed">
              +46 767266803
            </p>
          </div>
          <div>
            <p className="text-foreground-500 text-xs md:text-sm uppercase tracking-widest font-medium mb-3">Email</p>
            <p className="text-foreground-800 text-base md:text-lg leading-relaxed">
              info@zottermanconsulting.com
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}